/**
 * display-feed.ts — Types et logique du feed d'affichage (refonte display_queue)
 *
 * Deux couches d'affichage (Option A) :
 *   1. File du jour (display_queue) — contenu éditorial du board, ordre position ASC
 *   2. Fenêtres d'archives — photos des jours précédents, shuffle exhaustif sans répétition
 *
 * La tablette gère son propre état de fenêtre localement (ArchiveState).
 * Le serveur fournit le pool complet d'archives à chaque fetch du feed.
 */

// ─── Types API (format renvoyé par GET /api/display/:slug) ───────────────────

/** Item de la file éditoriale du jour (display_queue). */
export type QueueItem = {
  /** ID du record display_queue */
  id: string;
  position: number;
  content_type: "photo" | "message" | "weather_card";
  content_id: string;
  /** Données résolues par le serveur (caption/image_url, text/author, snapshot...) */
  data: Record<string, unknown>;
  created: string;
};

/** Photo du pool d'archives (jours précédents). */
export type ArchivePhoto = {
  id: string;
  caption?: string;
  created: string;
};

/** Payload complet renvoyé par le serveur. */
export type FeedPayload = {
  board: {
    id?: string;
    name?: string;
    slug?: string;
    settings?: Record<string, unknown>;
  };
  /** File du jour, triée par position ASC. */
  queue: QueueItem[];
  /** Pool de photos archivées (jours précédents), triées -created. */
  archive: ArchivePhoto[];
  /** Taille de la fenêtre d'archives (configurée sur le board, défaut 50). */
  archive_window_size: number;
  heartbeat_interval?: number;
};

// ─── Type carousel (ce que DisplayCarousel consomme) ─────────────────────────

/** Item normalisé pour le carousel d'affichage. */
export type FeedItem = {
  /** Identité stable pour React (content_id pour queue, photo id pour archives). */
  id: string;
  type: "photo" | "message" | "weather_card";
  data: Record<string, unknown>;
  created: string;
  /** Marqueur pour distinguer provenance (utile pour debug / vue admin). */
  source: "queue" | "archive";
};

// ─── Conversion queue → FeedItem ─────────────────────────────────────────────

export function queueItemToFeedItem(item: QueueItem): FeedItem {
  return {
    id: item.content_id,
    type: item.content_type,
    data: item.data,
    created: item.created,
    source: "queue",
  };
}

// ─── Archives — état local de la tablette ─────────────────────────────────────

/**
 * État local de la fenêtre d'archives.
 * Persisté en mémoire (perdu si la page recharge — acceptable pour un kiosk).
 */
export type ArchiveState = {
  /**
   * IDs des photos des fenêtres précédentes dans ce cycle.
   * Sert à éviter les répétitions jusqu'à épuisement complet du pool.
   */
  seenIds: string[];
  /**
   * IDs de la fenêtre courante (N photos en cours d'affichage).
   * Ces IDs sont dans `displayItems` côté carousel.
   */
  currentWindow: string[];
};

/** État initial : fenêtre vide, rien de vu. */
export function initArchiveState(): ArchiveState {
  return { seenIds: [], currentWindow: [] };
}

/**
 * Construit la première fenêtre à partir du pool d'archives.
 * À appeler au premier chargement du feed si l'état est vide.
 */
export function initializeArchiveWindow(
  archive: ArchivePhoto[],
  windowSize: number,
  seed: number,
): ArchiveState {
  const window = pickNextWindow([], archive.map((p) => p.id), windowSize, seed);
  return { seenIds: [], currentWindow: window };
}

/**
 * Avance la fenêtre d'archives après un cycle complet du carousel.
 *
 * - Marque la fenêtre courante comme "vue" (→ seenIds)
 * - Si le pool est épuisé (tous les IDs vus) → repart de zéro (nouveau cycle)
 * - Sinon → choisit les N prochains IDs non encore vus
 *
 * Le seed est la date du jour (change chaque jour pour un ordre différent).
 */
export function advanceArchiveWindow(
  state: ArchiveState,
  archive: ArchivePhoto[],
  windowSize: number,
  seed: number,
): ArchiveState {
  const allIds = archive.map((p) => p.id);
  const newSeenIds = [...state.seenIds, ...state.currentWindow];

  // Pool épuisé → reset du cycle complet
  const remainingIds = allIds.filter((id) => !newSeenIds.includes(id));
  if (remainingIds.length === 0) {
    const newWindow = pickNextWindow([], allIds, windowSize, seed);
    return { seenIds: [], currentWindow: newWindow };
  }

  const newWindow = pickNextWindow(newSeenIds, allIds, windowSize, seed);
  return { seenIds: newSeenIds, currentWindow: newWindow };
}

/**
 * Sélectionne les N prochains IDs non encore vus, dans un ordre aléatoire
 * déterministe (graine = date du jour pour que chaque tablette ait le même
 * ordre de départ tout en variant chaque jour).
 */
function pickNextWindow(seenIds: string[], allIds: string[], windowSize: number, seed: number): string[] {
  const remaining = allIds.filter((id) => !seenIds.includes(id));
  const shuffled = seededShuffle(remaining, seed + seenIds.length); // seed légèrement différent par fenêtre
  return shuffled.slice(0, windowSize);
}

/**
 * Convertit la fenêtre courante en FeedItems pour le carousel.
 * Filtre silencieusement les IDs qui n'existent plus dans le pool
 * (photo supprimée entre deux fetch du feed).
 */
export function archiveWindowToFeedItems(
  state: ArchiveState,
  archive: ArchivePhoto[],
  boardSlug: string,
): FeedItem[] {
  const archiveById = new Map(archive.map((p) => [p.id, p]));
  return state.currentWindow
    .map((id) => {
      const photo = archiveById.get(id);
      if (!photo) return null;
      return {
        id: photo.id,
        type: "photo" as const,
        data: {
          caption: photo.caption ?? "",
          image_url: `/api/boards/${boardSlug}/photos/${photo.id}/file`,
        },
        created: photo.created,
        source: "archive" as const,
      };
    })
    .filter((item): item is FeedItem => item !== null);
}

// ─── Utilitaires date ─────────────────────────────────────────────────────────

/**
 * Normalise une date PocketBase en timestamp ms.
 * PocketBase émet "2026-05-28 20:07:35.123Z" (espace à la place de T).
 * Retourne 0 pour les chaînes vides ou invalides.
 */
export function parseRecordDate(s: string): number {
  if (!s) return 0;
  const normalized = s.includes("T") ? s : s.replace(" ", "T") + (s.endsWith("Z") ? "" : "Z");
  const ms = new Date(normalized).getTime();
  return isNaN(ms) ? 0 : ms;
}

export function dateSeed(d: Date): number {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// ─── PRNG déterministe (mulberry32) ──────────────────────────────────────────

function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededShuffle<T>(arr: T[], seed: number): T[] {
  const result = [...arr];
  const random = mulberry32(seed);
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
